import type { PlanFeatureKey } from '@/api/billing.api';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  User,
  Bell,
  Shield,
  Clock,
  DoorOpen,
  CreditCard,
  Percent,
  Receipt,
  Coins,
  ReceiptText,
  Undo2,
} from 'lucide-react';
import type { Permission } from '@/types/auth.types';
import { hasPermission, ActionsEnum } from '@/lib/permissions';
import type { SettingsSection as SectionId } from '@/routes/route-config';
import {
  DcBranchesCurrenciesIcon,
  DcCategoryClinicIcon,
  DcClinicWebPageIcon,
  DcConsentFormsIcon,
  DcDentalChartIcon,
  DcFinanceAccountsIcon,
  DcInstalmentsIcon,
  DcLoyaltyOffersIcon,
  DcMedicalHistoryQrIcon,
  DcOnlineBookingIcon,
  DcPrescriptionsIcon,
  DcRecallsIcon,
  DcTeamPermissionsIcon,
  DcXRaysPhotosIcon,
} from '@/components/ds/icons/DentaCaresIcons';

/**
 * The settings sections, in one place.
 *
 * Previously this knowledge was split three ways and drifted: `Settings.tsx`
 * held labels and icons, `nav-config.tsx` held a second copy for the sidebar,
 * and `isTabAllowed` held a hand-written if-chain of permission rules. A
 * section added to one was routinely missing from another. Now the nav, the
 * router and the guard all read this array.
 *
 * `component` is lazy so a section's code — and the requests it fires on mount
 * — arrive only when that section is opened. Statically importing all 19 was
 * what made Settings load the whole product.
 */
export interface SettingsSectionSpec {
  id: SectionId;
  labelKey: string;
  descriptionKey: string;
  group: SettingsGroupId;
  icon: ComponentType<{ className?: string }>;
  /**
   * Resource gating access. `null` means every member may see it — these are
   * the sections about *yourself* (profile, security) or about the account as
   * a whole (subscription), not about the clinic's data.
   */
  resource: string | null;
  /** A plan feature the section configures; without it the upgrade panel shows instead. */
  feature?: PlanFeatureKey;
  component: LazyExoticComponent<ComponentType>;
}

/**
 * Twenty-odd sections read as a wall when listed flat. They are grouped by
 * what they shape: the signed-in person, the clinic's setup, its team, its
 * treatments, what patients see and pay when they book, and its money.
 * Anything that is read rather than configured (the activity log) lives in
 * the main navigation, not here. The array order below is
 * the display order, group by group.
 */
export type SettingsGroupId =
  | 'account'
  | 'clinic'
  | 'team'
  | 'care'
  | 'booking'
  | 'billing';

export const SETTINGS_GROUPS: { id: SettingsGroupId; labelKey: string }[] = [
  { id: 'account', labelKey: 'settings.groups.account' },
  { id: 'clinic', labelKey: 'settings.groups.clinic' },
  { id: 'team', labelKey: 'settings.groups.team' },
  { id: 'care', labelKey: 'settings.groups.care' },
  { id: 'booking', labelKey: 'settings.groups.booking' },
  { id: 'billing', labelKey: 'settings.groups.billing' },
];

const named = <M, K extends keyof M>(loader: () => Promise<M>, key: K) =>
  lazy(() =>
    loader().then((m) => ({ default: m[key] as ComponentType })),
  ) as LazyExoticComponent<ComponentType>;

export const SETTINGS_SECTION_SPECS: SettingsSectionSpec[] = [
  {
    id: 'profile',
    labelKey: 'settings.profile.title',
    descriptionKey: 'settings.profile.description',
    group: 'account',
    icon: User,
    resource: null,
    component: named(
      () => import('@/components/profile/ProfileSettings'),
      'ProfileSettings',
    ),
  },
  {
    id: 'notifications',
    labelKey: 'settings.notifications.title',
    descriptionKey: 'settings.notifications.description',
    group: 'account',
    icon: Bell,
    resource: null,
    component: named(
      () => import('@/components/notifications/NotificationsSettings'),
      'NotificationsSettings',
    ),
  },
  {
    id: 'clinic',
    labelKey: 'settings.clinic.title',
    descriptionKey: 'settings.clinic.description',
    group: 'clinic',
    icon: DcCategoryClinicIcon,
    resource: 'clinic',
    component: named(
      () => import('@/components/clinic/ClinicSettings'),
      'ClinicSettings',
    ),
  },
  {
    id: 'branches',
    labelKey: 'settings.branches.title',
    descriptionKey: 'settings.branches.description',
    group: 'clinic',
    icon: DcBranchesCurrenciesIcon,
    resource: 'branches',
    component: named(
      () => import('@/components/branches/BranchSettings'),
      'BranchSettings',
    ),
  },
  {
    id: 'rooms',
    labelKey: 'settings.rooms.title',
    descriptionKey: 'settings.rooms.description',
    group: 'clinic',
    icon: DoorOpen,
    resource: 'rooms',
    component: named(
      () => import('@/components/rooms/RoomSettings'),
      'RoomSettings',
    ),
  },
  {
    id: 'availability',
    labelKey: 'settings.availability.title',
    descriptionKey: 'settings.availability.description',
    group: 'clinic',
    icon: Clock,
    resource: 'settings',
    component: named(
      () => import('@/components/clinic/AvailabilitySettings'),
      'AvailabilitySettings',
    ),
  },
  {
    id: 'xray-importer',
    feature: 'xray_import',
    labelKey: 'settings.xrayImporter.title',
    descriptionKey: 'settings.xrayImporter.description',
    group: 'clinic',
    icon: DcXRaysPhotosIcon,
    resource: null,
    component: named(
      () => import('@/components/xray/XrayImporterSettings'),
      'XrayImporterSettings',
    ),
  },
  {
    id: 'staff',
    labelKey: 'settings.staff.title',
    descriptionKey: 'settings.staff.description',
    group: 'team',
    icon: DcTeamPermissionsIcon,
    resource: 'staff',
    component: named(
      () => import('@/components/staff/StaffSettings'),
      'StaffSettings',
    ),
  },
  {
    id: 'roles',
    labelKey: 'settings.roles.title',
    descriptionKey: 'settings.roles.description',
    group: 'team',
    icon: Shield,
    resource: 'roles',
    component: named(
      () => import('@/components/roles/RoleSettings'),
      'RoleSettings',
    ),
  },
  {
    id: 'procedures',
    labelKey: 'settings.procedures.title',
    descriptionKey: 'settings.procedures.description',
    group: 'care',
    icon: DcDentalChartIcon,
    resource: 'procedures',
    component: named(
      () => import('@/components/procedures/ProcedureSettings'),
      'ProcedureSettings',
    ),
  },
  {
    // Completed visits put patients on recall. Anyone with settings access
    // reads it; changing it needs settings:manage (enforced by the API).
    id: 'recalls',
    feature: 'auto_recalls',
    labelKey: 'settings.recalls.title',
    descriptionKey: 'settings.recalls.description',
    group: 'care',
    icon: DcRecallsIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/recalls/RecallSettings'),
      'RecallSettings',
    ),
  },
  {
    id: 'medical-history',
    labelKey: 'settings.medicalForm.title',
    descriptionKey: 'settings.medicalForm.description',
    group: 'care',
    icon: DcMedicalHistoryQrIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/medical-history/MedicalFormSettings'),
      'MedicalFormSettings',
    ),
  },
  {
    id: 'prescriptions',
    labelKey: 'settings.prescriptions.title',
    descriptionKey: 'settings.prescriptions.description',
    group: 'care',
    icon: DcPrescriptionsIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/prescriptions'),
      'PrescriptionSettingsTab',
    ),
  },
  {
    id: 'consent-forms',
    feature: 'consent_esign',
    labelKey: 'settings.consentForms.title',
    descriptionKey: 'settings.consentForms.description',
    group: 'care',
    icon: DcConsentFormsIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/consent/ConsentTemplatesSettings'),
      'ConsentTemplatesSettings',
    ),
  },
  {
    id: 'booking',
    labelKey: 'settings.booking.title',
    descriptionKey: 'settings.booking.description',
    group: 'booking',
    icon: DcOnlineBookingIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/booking/BookingSettings'),
      'BookingSettings',
    ),
  },
  {
    id: 'landing-page',
    labelKey: 'settings.landingPage.title',
    descriptionKey: 'settings.landingPage.description',
    group: 'booking',
    icon: DcClinicWebPageIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/booking/landing/LandingPageSettings'),
      'LandingPageSettings',
    ),
  },
  {
    id: 'reservation-fees',
    labelKey: 'settings.reservationFees.title',
    descriptionKey: 'settings.reservationFees.description',
    group: 'booking',
    icon: Receipt,
    resource: 'settings',
    component: named(
      () => import('@/components/reservations/ReservationFeesSettings'),
      'ReservationFeesSettings',
    ),
  },
  {
    id: 'offers',
    feature: 'loyalty_offers',
    labelKey: 'settings.offers.title',
    descriptionKey: 'settings.offers.description',
    group: 'booking',
    icon: Percent,
    resource: 'procedures',
    component: named(
      () => import('@/components/procedures/OffersSettings'),
      'OffersSettings',
    ),
  },
  {
    id: 'loyalty',
    feature: 'loyalty_offers',
    labelKey: 'settings.loyalty.title',
    descriptionKey: 'settings.loyalty.description',
    group: 'booking',
    icon: DcLoyaltyOffersIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/loyalty/LoyaltySettings'),
      'LoyaltySettings',
    ),
  },
  {
    id: 'subscription',
    labelKey: 'settings.subscription.title',
    descriptionKey: 'settings.subscription.description',
    group: 'billing',
    icon: CreditCard,
    // The billing API answers finance:view only. Open to everyone, this was
    // the first section a dentist landed on, and it greeted them with "We
    // could not load your billing details".
    resource: 'finance',
    component: named(
      () => import('@/components/billing/BillingSettings'),
      'BillingSettings',
    ),
  },
  {
    // What the payment receipt shows and in what order. Anyone with finance
    // access reads it; changing it needs finance:manage (enforced by the API).
    id: 'receipts',
    labelKey: 'settings.receipts.title',
    descriptionKey: 'settings.receipts.description',
    group: 'billing',
    icon: ReceiptText,
    resource: 'finance',
    component: named(
      () => import('@/components/receipts/ReceiptSettings'),
      'ReceiptSettings',
    ),
  },
  {
    // When the clinic gives money back: treatment refunds and patient credit
    // payouts. Finance view reads it; changing it needs finance:manage.
    id: 'refunds',
    labelKey: 'settings.refunds.title',
    descriptionKey: 'settings.refunds.description',
    group: 'billing',
    icon: Undo2,
    resource: 'finance',
    component: named(
      () => import('@/components/finances/RefundSettings'),
      'RefundSettings',
    ),
  },
  {
    // How patients may pay for a presented treatment plan: a pay-in-full
    // discount and the instalment plans offered. Changing it needs
    // settings:manage (enforced by the API).
    id: 'plan-payments',
    labelKey: 'settings.planPayments.title',
    descriptionKey: 'settings.planPayments.description',
    group: 'billing',
    icon: DcInstalmentsIcon,
    resource: 'settings',
    component: named(
      () => import('@/components/treatment-plan/PlanPaymentTermsSettings'),
      'PlanPaymentTermsSettings',
    ),
  },
  {
    // Branch currencies and exchange rates. Anyone with finance access reads
    // it; changing it needs finance:manage (enforced by the API too).
    id: 'currency',
    labelKey: 'settings.currencySettings.title',
    descriptionKey: 'settings.currencySettings.description',
    group: 'billing',
    icon: Coins,
    resource: 'finance',
    component: named(
      () => import('@/components/currency/CurrencySettings'),
      'CurrencySettings',
    ),
  },
  {
    // How costs are counted — today, when materials become a cost. Anyone
    // with finance access reads it; changing it needs finance:manage.
    id: 'finance',
    labelKey: 'settings.expenseSettings.title',
    descriptionKey: 'settings.expenseSettings.description',
    group: 'billing',
    icon: DcFinanceAccountsIcon,
    resource: 'finance',
    component: named(
      () => import('@/components/finances/ExpenseSettings'),
      'ExpenseSettings',
    ),
  },
];

export function findSectionSpec(id: string | undefined) {
  return SETTINGS_SECTION_SPECS.find((s) => s.id === id);
}

export function canSeeSection(
  spec: SettingsSectionSpec,
  permissions: Permission[] | undefined,
): boolean {
  return (
    !spec.resource ||
    hasPermission(permissions, spec.resource, ActionsEnum.VIEW)
  );
}

/** The sections this user may open, in display order. */
export function visibleSections(permissions: Permission[] | undefined) {
  return SETTINGS_SECTION_SPECS.filter((s) => canSeeSection(s, permissions));
}

/** Sections bucketed under their group headings; empty groups are dropped. */
export function groupSections(sections: SettingsSectionSpec[]) {
  return SETTINGS_GROUPS.map((group) => ({
    ...group,
    sections: sections.filter((s) => s.group === group.id),
  })).filter((group) => group.sections.length > 0);
}
